//This is the API for the current user's account

import { User } from '../auth/User';
import { IUsersFilters, UserAccount } from '../types/UserAccount';
import { ApiParams, ApiRoutes, getFullApiRoute, replaceRouteParams } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';

export class UserAccountAPI {
  private static async fetchUsers(
    user: User,
    params: ApiParams,
    filters?: IUsersFilters,
    additionalParams?: Record<string, string>,
    systemAdminContext?: { isSystemAdmin: boolean; selectedCompanyId?: string },
  ): Promise<{ data: UserAccount[]; metadata?: unknown }> {
    const urlParams = new URLSearchParams({
      page: String(params.page || 1),
      perPage: String(params.per_page || 10),
      ...additionalParams,
    });

    if (params.searchKeyword) urlParams.append('search', params.searchKeyword);
    if (filters?.status?.length) urlParams.append('statuses', filters.status.map(status => status.toLowerCase()).join(','));
    if (filters?.companies?.length) urlParams.append('companyIds', filters.companies.join(','));
    if (filters?.roles?.length) urlParams.append('roleIds', filters.roles.join(','));

    // Add company context when selectedCompanyId is provided (for system admin or account admin with company selected)
    if (systemAdminContext?.selectedCompanyId &&
      systemAdminContext?.selectedCompanyId !== 'system-admin') {
      urlParams.append('companyId', systemAdminContext.selectedCompanyId);
    }

    const requestURL = `${getFullApiRoute(ApiRoutes.USERS_V3)}?${urlParams.toString()}`;
    const result = await HttpRequest(requestURL, 'GET', null, null, user);

    return {
      data: (result?.data as UserAccount[]) || [],
      metadata: result?.metadata,
    };
  }

  //Get all user memebrships
  static async memberships(user: User): Promise<any[]> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.USER_MEMBERSHIPS), {
        id: user.attributes.userId,
      }),
      'GET',
      null,
      null,
      user,
    );
    return result?.data || [];
  }

  static async companies(
    user: User,
    userId?: string,
    searchTerm?: string,
    offset?: number,
  ): Promise<any[]> {
    const urlParams = new URLSearchParams();
    if (offset) urlParams.append('offset', String(offset));
    if (searchTerm) urlParams.append('searchTerm', searchTerm);

    const apiPath = `${getFullApiRoute(ApiRoutes.USER_COMPANIES)}?${urlParams.toString()}`;

    const result = await HttpRequest(
      replaceRouteParams(apiPath, {
        id: userId ? userId : user.attributes.id,
      }),
      'GET',
      null,
      null,
      user,
    );
    return result?.data || [];
  }

  static async updateDetails(user: User): Promise<any> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.CURRENT_USER_UPDATE_V3),
      'PUT',
      {
        name: user.attributes.name,
        surname: user.attributes.surname,
        phone: user.attributes.phone,
      },
      null,
      user,
    );
    return result?.data || {};
  }

  static async getSystemAdminUsers(
    user: User,
    params: ApiParams,
    filters?: IUsersFilters,
    systemAdminContext?: { isSystemAdmin: boolean; selectedCompanyId?: string },
  ): Promise<{ data: UserAccount[]; metadata?: unknown }> {
    return UserAccountAPI.fetchUsers(
      user,
      params,
      filters,
      { internal: 'true' },
      systemAdminContext,
    );
  }

  static async getAllCompanyUsers(
    user: User,
    params: ApiParams,
    filters?: IUsersFilters,
    systemAdminContext?: { isSystemAdmin: boolean; selectedCompanyId?: string },
  ): Promise<{ data: UserAccount[]; metadata?: unknown }> {
    return UserAccountAPI.fetchUsers(
      user,
      params,
      filters,
      { internal: 'false' },
      systemAdminContext,
    );
  }

  static async getCompanyUsers(
    user: User,
    companyUuid: string,
    params: ApiParams,
    filters?: IUsersFilters,
    systemAdminContext?: { isSystemAdmin: boolean; selectedCompanyId?: string },
  ): Promise<{ data: UserAccount[]; metadata?: unknown }> {
    return UserAccountAPI.fetchUsers(
      user,
      params,
      filters,
      { companyIds: companyUuid, internal: 'false' },
      systemAdminContext
    );
  }

  static async getRoles(user: User | null): Promise<Array<{ id: string; name: string }> | null> {
    const url = getFullApiRoute(ApiRoutes.USER_V3_ROLES);
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result && result.data ? result.data : null;
  }

  static async bulkResendInvite(
    user: User,
    payload: { users: Array<{ id: string; email: string }> },
  ): Promise<{ data: Array<{ requested: number; succeeded: number; failed: Array<{ email: string }>; skipped: number }> }> {
    const url = getFullApiRoute(ApiRoutes.BULK_RESEND_INVITE_V3);
    return await HttpRequest(url, 'POST', payload, null, user);
  }

  static async deletUsers(user: User, usersToDelete: UserAccount[]): Promise<boolean | undefined> {
    const userIds = usersToDelete.map((userAccount) => userAccount.id);

    const result = await HttpRequest(
      `${getFullApiRoute(ApiRoutes.USERS_V3)}/${userIds[0]}`,
      'DELETE',
      null,
      null,
      user,
    );

    return result && Array.isArray(result.data);
  }
}
